import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { EntityHistoryPanel } from "../components/EntityHistoryPanel";

type Status = "Draft" | "InReview" | "Authorised" | "Archived";

interface FileRow {
  uuid: string;
  label: string;
  ordinal: number;
  fileSizeBytes: number;
  fileMime: string;
  fileSha256: string;
  originalFilename: string;
  createdAt: string;
}

interface VersionRow {
  number: number;
  createdAt: string;
  files: FileRow[];
}

interface LinkedClause {
  uuid: string;
  standardCode: string;
  versionYear: number;
  clauseReference: string;
}

interface EvidenceDetailDto {
  uuid: string;
  documentTitle: string;
  description: string | null;
  status: Status;
  currentVersionNumber: number | null;
  fileCount: number;
  linkedClauses: LinkedClause[];
  isNetworkShared: boolean;
  authorisedAt: string | null;
  nextReviewDate: string | null;
  createdAt: string;
  updatedAt: string;
  files: FileRow[];
  versions: VersionRow[];
  allowedTransitions: Status[];
}

const STATUS_TONE: Record<Status, string> = {
  Draft: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
  InReview: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200",
  Authorised: "bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-200",
  Archived: "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
};

const STATUS_LABEL: Record<Status, string> = {
  Draft: "Draft",
  InReview: "In review",
  Authorised: "Authorised",
  Archived: "Archived",
};

const TRANSITION_LABEL: Record<Status, string> = {
  Draft: "Move back to Draft",
  InReview: "Submit for review",
  Authorised: "Authorise",
  Archived: "Archive",
};

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDateUk = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

export function EvidenceDetail() {
  const { slug, uuid } = useParams<{ slug: string; uuid: string }>();
  const navigate = useNavigate();
  const { activeOrganisation } = useAuth();
  const isAdmin = activeOrganisation?.role === "Admin";
  const [data, setData] = useState<EvidenceDetailDto | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [transitionComment, setTransitionComment] = useState("");
  const [pendingTransition, setPendingTransition] = useState<Status | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingUnpublish, setConfirmingUnpublish] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    if (!uuid) return;
    try {
      const res = await api<{ evidence: EvidenceDetailDto }>(`/evidence/${uuid}`);
      setData(res.evidence);
    } catch (err) {
      setErrors(formatApiError(err));
    }
  }, [uuid]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (errors.length > 0 && !data) {
    return (
      <div
        role="alert"
        className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
      >
        {errors[0]}
      </div>
    );
  }

  if (!data) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;
  }

  const isMutable = data.status === "Draft";

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;
    setBusy(true);
    setErrors([]);
    try {
      const formData = new FormData();
      for (const f of Array.from(fileList)) formData.append("files", f);
      await api(`/evidence/${data.uuid}/files`, {
        method: "POST",
        body: formData,
      });
      await refresh();
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const removeFile = async (fileUuid: string) => {
    setBusy(true);
    setErrors([]);
    try {
      await api(`/evidence/${data.uuid}/files/${fileUuid}`, { method: "DELETE" });
      await refresh();
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitTransition = async (event: FormEvent) => {
    event.preventDefault();
    if (!pendingTransition) return;
    setBusy(true);
    setErrors([]);
    try {
      await api(`/evidence/${data.uuid}/transition`, {
        method: "POST",
        body: { to: pendingTransition, comment: transitionComment.trim() || null },
      });
      setPendingTransition(null);
      setTransitionComment("");
      await refresh();
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const setPublished = async (publish: boolean) => {
    setBusy(true);
    setErrors([]);
    try {
      await api(`/evidence/${data.uuid}/publish`, {
        method: "POST",
        body: { publish },
      });
      setConfirmingUnpublish(false);
      await refresh();
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteEvidence = async () => {
    setBusy(true);
    setErrors([]);
    try {
      await api(`/evidence/${data.uuid}`, { method: "DELETE" });
      navigate(`/o/${slug}/vault`);
    } catch (err) {
      setErrors(formatApiError(err));
      setBusy(false);
      setConfirmingDelete(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link
            to={`/o/${slug}/vault`}
            className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300"
          >
            ← The Vault
          </Link>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {data.documentTitle}
          </h1>
          {data.description ? (
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{data.description}</p>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-2">
          <span
            className={`rounded px-2 py-0.5 text-xs font-medium uppercase tracking-wide ${STATUS_TONE[data.status]}`}
          >
            {STATUS_LABEL[data.status]}
          </span>
          {data.currentVersionNumber ? (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Current version: v{data.currentVersionNumber}
            </span>
          ) : null}
        </div>
      </div>

      {errors.length > 0 ? (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          {errors.length === 1 ? (
            errors[0]
          ) : (
            <ul className="list-disc space-y-0.5 pl-4">
              {errors.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      <section className="grid gap-3 sm:grid-cols-3">
        <Card label="Linked clauses" value={data.linkedClauses.length.toString()} />
        <Card label="Authorised on" value={formatDateUk(data.authorisedAt)} />
        <Card label="Next review" value={formatDateUk(data.nextReviewDate)} />
      </section>

      {data.linkedClauses.length > 0 ? (
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Linked clauses
          </h2>
          <div className="mt-2 flex flex-wrap gap-2">
            {data.linkedClauses.map((c) => (
              <Link
                key={c.uuid}
                to={`/o/${slug}/standards/${c.standardCode}/${c.versionYear}/${encodeURIComponent(c.clauseReference)}`}
                className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700 hover:border-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                {c.standardCode.replace(/^ISO/, "ISO ")}:{c.versionYear} cl. {c.clauseReference}
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <section>
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Files
          </h2>
          {isMutable ? (
            <label className="inline-flex cursor-pointer items-center rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700">
              {busy ? "Uploading…" : "Add files"}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/png,image/jpeg"
                onChange={handleUpload}
                className="hidden"
              />
            </label>
          ) : null}
        </div>
        {data.files.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">No files yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white dark:divide-slate-700 dark:border-slate-700 dark:bg-slate-800">
            {data.files.map((f) => (
              <li key={f.uuid} className="flex items-center justify-between gap-3 p-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                    {f.label}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                    {f.originalFilename} · {formatBytes(f.fileSizeBytes)} ·{" "}
                    <span title={`SHA-256: ${f.fileSha256}`} className="font-mono">
                      {f.fileSha256.slice(0, 12)}…
                    </span>
                  </p>
                </div>
                <a
                  href={`${import.meta.env.VITE_API_URL ?? "/api/v1"}/evidence/${data.uuid}/files/${f.uuid}/download`}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  Download
                </a>
                {isMutable ? (
                  <button
                    type="button"
                    onClick={() => void removeFile(f.uuid)}
                    disabled={busy}
                    className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-950/40"
                  >
                    Remove
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Workflow
        </h2>
        <div className="mt-2 flex flex-wrap gap-2">
          {data.allowedTransitions.length === 0 ? (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              No further transitions available from this status.
            </p>
          ) : (
            data.allowedTransitions.map((to) => (
              <button
                key={to}
                type="button"
                onClick={() => setPendingTransition(to)}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-brand-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
              >
                {TRANSITION_LABEL[to]}
              </button>
            ))
          )}
        </div>

        {pendingTransition ? (
          <form
            onSubmit={submitTransition}
            className="mt-3 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800"
          >
            <p className="text-xs text-slate-600 dark:text-slate-400">
              Transitioning from <strong>{STATUS_LABEL[data.status]}</strong> to{" "}
              <strong>{STATUS_LABEL[pendingTransition]}</strong>
              {pendingTransition === "Authorised"
                ? " — this will create an immutable version snapshot of the current files."
                : ""}
            </p>
            <textarea
              value={transitionComment}
              onChange={(e) => setTransitionComment(e.target.value)}
              placeholder="Optional comment"
              rows={2}
              className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            />
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={busy}
                className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
              >
                {busy ? "Working…" : "Confirm"}
              </button>
              <button
                type="button"
                onClick={() => setPendingTransition(null)}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}
      </section>

      {data.status === "Authorised" && isAdmin ? (
        <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Network publishing
          </h2>
          {data.isNetworkShared ? (
            <div className="mt-2 space-y-2">
              <p className="text-sm text-slate-700 dark:text-slate-200">
                Published to your Supplier Trust Network. Buyers connected to{" "}
                <span className="font-medium">{slug}</span> can see this record and pin a copy to
                their own vault.
              </p>
              {confirmingUnpublish ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/40 dark:bg-amber-500/10">
                  <p className="text-xs text-amber-900 dark:text-amber-100">
                    Unpublishing hides this from new viewers. Buyers who already pinned a copy keep
                    theirs (immutable snapshot). Continue?
                  </p>
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => void setPublished(false)}
                      disabled={busy}
                      className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
                    >
                      {busy ? "Working…" : "Yes, unpublish"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmingUnpublish(false)}
                      className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmingUnpublish(true)}
                  disabled={busy}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                >
                  Unpublish from network
                </button>
              )}
            </div>
          ) : (
            <div className="mt-2 space-y-2">
              <p className="text-sm text-slate-700 dark:text-slate-200">
                Not published. Authorised evidence is private to your organisation by default.
                Publishing makes it visible to buyers who have an active connection with you.
              </p>
              <button
                type="button"
                onClick={() => void setPublished(true)}
                disabled={busy}
                className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
              >
                {busy ? "Working…" : "Publish to network"}
              </button>
            </div>
          )}
        </section>
      ) : null}

      <EntityHistoryPanel entityType="evidence_vault" entityUuid={data.uuid} />

      {data.versions.length > 0 ? (
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Authorised version history
          </h2>
          <ul className="mt-2 space-y-2">
            {data.versions
              .slice()
              .reverse()
              .map((v) => (
                <li
                  key={v.number}
                  className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      v{v.number}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {formatDateUk(v.createdAt)}
                    </p>
                  </div>
                  <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                    {v.files.length} file{v.files.length === 1 ? "" : "s"} captured at this version
                  </p>
                </li>
              ))}
          </ul>
        </section>
      ) : null}

      {data.status !== "Authorised" ? (
        <section className="border-t border-slate-200 pt-4 dark:border-slate-700">
          {confirmingDelete ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/40">
              <p className="text-sm font-medium text-red-900 dark:text-red-100">
                Delete &quot;{data.documentTitle}&quot;?
              </p>
              <p className="mt-1 text-xs text-red-700 dark:text-red-200">
                This soft-deletes the record. Files on disk are retained so older versions stay
                reproducible.
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => void deleteEvidence()}
                  disabled={busy}
                  className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                >
                  {busy ? "Deleting…" : "Yes, delete"}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(false)}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                disabled={busy}
                className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60 dark:border-red-800 dark:bg-slate-800 dark:text-red-200 dark:hover:bg-red-950/40"
              >
                Delete evidence
              </button>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Cannot be deleted once Authorised — archive instead to preserve the audit trail.
              </p>
            </>
          )}
        </section>
      ) : null}
    </div>
  );
}

function Card({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className="mt-1 text-base font-semibold text-slate-900 dark:text-slate-100">{value}</p>
    </div>
  );
}
